import React, { useEffect, useState, useRef } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import Picker from 'emoji-picker-react';
import { SpeedDial, SpeedDialAction, Box, IconButton } from '@mui/material';

export default function MessageInput(props) {
    const [currentValue, setCurrentValue] = useState(props.inputValue || '');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const inputRef = useRef(null);
    const speedDialRef = useRef(null);

    useEffect(() => {
        setCurrentValue(props.inputValue || '');
    }, [props.inputValue]);

    const onEmojiClick = (emojiObject) => {
        const newValue = currentValue + emojiObject.emoji;
        setCurrentValue(newValue);
        if (props.setInput) {
            props.setInput({ target: { value: newValue } });
        }
        setShowEmojiPicker(false);
    };

    const onHoverAction = (e, action) => {
        if (document.getElementById('file') == null) return;
        if (action.datatype === 'Archivo') {
            document.getElementById('file').setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.mp3,.wav,.ogg,.mp4,.avi,.mkv,.mov,.wmv,.flv,.webm,.jpg,.jpeg,.png,.gif,.svg');
            document.getElementById('file').setAttribute('data-type', 'file');
        } else if (action.datatype === 'Imagen') {
            document.getElementById('file').setAttribute('accept', '.jpg,.jpeg,.png,.gif,.svg');
            document.getElementById('file').setAttribute('data-type', 'image');
        } else if (action.datatype === 'Video') {
            document.getElementById('file').setAttribute('accept', '.mp4,.avi,.mkv,.mov,.wmv,.flv,.webm');
            document.getElementById('file').setAttribute('data-type', 'video');
        }
    };

    const handleSpeedDialAction = (action) => {
        onHoverAction(null, { datatype: action.name });
        handleClick();
        setSpeedDialOpen(false);
    };

    const onChangeValue = (e) => {
        setCurrentValue(e.target.value);
        if (props.setInput) props.setInput(e);
    };

    const onEnterPress = (e) => {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault();
            onSubmit(e);
        }
    };

    const handleClick = (e) => {
        if (e) e.preventDefault();
        if (inputRef && inputRef.current) inputRef.current.click();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (speedDialRef.current && !speedDialRef.current.contains(event.target)) {
                setSpeedDialOpen(false);
            }
        };

        if (speedDialOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [speedDialOpen]);

    const actions = [
        { icon: <InsertDriveFileIcon sx={{ color: '#008069' }} />, name: 'Archivo', onClick: (e) => onHoverAction(e, { datatype: 'Archivo' }) },
        { icon: <ImageIcon sx={{ color: '#008069' }} />, name: 'Imagen', onClick: (e) => onHoverAction(e, { datatype: 'Imagen' }) },
        { icon: <VideoLibraryIcon sx={{ color: '#008069' }} />, name: 'Video', onClick: (e) => onHoverAction(e, { datatype: 'Video' }) },
    ];

    const onSubmit = async (e) => {
        e.preventDefault();
        await props.onSubmit(e);
        setCurrentValue('');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Hidden File Input */}
            <form encType='multipart/form-data'>
                <input
                    type="file"
                    id="file"
                    ref={inputRef}
                    className="hidden"
                    data-type="file"
                    onChange={props.onFileUpload}
                />
            </form>

            {/* Emoji Picker Popover */}
            {showEmojiPicker && (
                <Box sx={{ position: 'absolute', bottom: '90px', left: '24px', zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: '16px' }}>
                    <Picker onEmojiClick={onEmojiClick} />
                </Box>
            )}

            {/* Floating Text Bar Container */}
            <form onSubmit={onSubmit} id="formtext" style={{ width: '100%' }}>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #e9edef', 
                        borderRadius: '28px', 
                        px: 2, 
                        py: 0.8,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                        transition: 'all 0.2s ease',
                        '&:focus-within': {
                            backgroundColor: '#ffffff',
                            borderColor: '#e9edef',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    {/* Attach Icon with SpeedDial */}
                    <Box ref={speedDialRef} sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                            size="small"
                            onClick={() => setSpeedDialOpen(!speedDialOpen)}
                            sx={{ color: '#8696a0', p: 0.8, '&:hover': { color: '#008069', backgroundColor: 'rgba(0,128,105,0.08)' } }}
                        >
                            <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
                        </IconButton>

                        <SpeedDial
                            open={speedDialOpen}
                            onOpen={() => setSpeedDialOpen(true)}
                            onClose={() => setSpeedDialOpen(false)}
                            FabProps={{ size: 'small', style: { display: 'none' } }}
                            ariaLabel="Attachment options"
                            sx={{ position: 'absolute', bottom: 35, left: -10, zIndex: 999 }}
                        >
                            {actions.map((action) => (
                                <SpeedDialAction
                                    key={action.name}
                                    icon={action.icon}
                                    tooltipTitle={action.name}
                                    onClick={() => handleSpeedDialAction(action)}
                                    datatype={action.name}
                                />
                            ))}
                        </SpeedDial>
                    </Box>

                    {/* Emoji Icon */}
                    <IconButton 
                        size="small"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        sx={{ color: '#8696a0', p: 0.8, mr: 1, '&:hover': { color: '#008069', backgroundColor: 'rgba(0,128,105,0.08)' } }}
                    >
                        <InsertEmoticonIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Text Input Area */}
                    <textarea
                        value={currentValue}
                        onChange={(e) => onChangeValue(e)}
                        className="w-full bg-transparent outline-none placeholder-gray-400 text-gray-800 font-normal text-sm resize-none focus:outline-none focus:ring-0 focus:border-transparent border-none shadow-none"
                        style={{ minHeight: '24px', maxHeight: '100px', paddingTop: '4px', outline: 'none', boxShadow: 'none' }}
                        type="text"
                        placeholder={props.hasAttachment ? "Escriba un mensaje o descripción para acompañar su archivo aquí..." : "Escribe un mensaje..."}
                        rows={1}
                        onKeyDown={(e) => onEnterPress(e)}
                    />

                    {/* Send Button */}
                    <IconButton 
                        type="submit" 
                        size="small"
                        sx={{ 
                            color: (currentValue && currentValue.trim()) || props.hasAttachment ? '#008069' : '#8696a0', 
                            p: 0.8,
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                                color: '#008069', 
                                backgroundColor: 'rgba(0,128,105,0.08)',
                                transform: 'scale(1.08)'
                            } 
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', transform: 'rotate(45deg)', marginLeft: '-2px' }}>
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </IconButton>
                </Box>
            </form>
        </Box>
    );
}